import React, { useMemo, useState, useCallback, useEffect } from "react";
import * as Yup from "yup";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormSelect from "../common/FormSelect";
import FormInput from "../common/FormInput";
import {
  InformationCircleIcon,
  DocumentIcon,
  HandThumbUpIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import FileUploader from "../common/FileUploader";
import { useFetch, useSubmission } from "../../hooks/useCallApi";
import { showSuccess, showError } from "../../utils/notificationHelper";

// ---------------------------------------------------------------------------
// Yup schemas per step
// ---------------------------------------------------------------------------
const stepSchemas = [
  // Step 0 — Information
  Yup.object({
    application: Yup.object({
      business_id: Yup.number()
        .typeError("Business is required")
        .required("Business is required")
        .nullable(),
      application_type: Yup.string()
        .oneOf(["new", "renewal", "closure"], "Invalid application type")
        .required("Application type is required"),
      permit_year: Yup.number()
        .typeError("Permit year must be a number")
        .integer("Permit year must be a whole number")
        .min(2000, "Permit year must be 2000 or later")
        .max(2100, "Permit year must be 2100 or earlier")
        .required("Permit year is required"),
      remarks: Yup.string().optional(),
    }),
  }),

  // Step 1 — Documents
  Yup.object({
    documents: Yup.array()
      .optional(),
  }),

  // Step 2 — Endorsement
  Yup.object({
    endorsement: Yup.object({
      status: Yup.string()
        .oneOf(["pending", "returned", "endorsed"], "Invalid status")
        .required("Endorsement status is required"),
      remarks: Yup.string().optional(),
      endorsed_by: Yup.mixed().optional().nullable(),
    }),
  }),

  // Step 3 — Validity
  Yup.object({
    business_permit: Yup.object({
      status: Yup.string()
        .oneOf(["pending", "active", "expired", "renewed", "revoked"], "Invalid status")
        .required("Permit status is required"),
      issued_date: Yup.string().optional().nullable(),
      valid_from: Yup.string().optional().nullable(),
      valid_until: Yup.string()
        .optional()
        .nullable()
        .test(
          "valid_until_after_valid_from",
          "Valid Until must be after Valid From",
          function (value) {
            const { valid_from } = this.parent;
            if (!value || !valid_from) return true;
            return new Date(value) >= new Date(valid_from);
          }
        ),
    }),
  }),
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const buildInitialValues = (mode, initialData) => {
  const isEdit = mode === "edit";
  const isRenewal = mode === "renewal";

  // Use initialData for both edit and renewal modes
  const baseApplication = (isEdit || isRenewal) ? initialData ?? {} : {};
  const baseEndorsement = (isEdit || isRenewal) ? initialData?.endorsement ?? {} : {};
  const baseBusinessPermit = (isEdit || isRenewal) ? initialData?.business_permit ?? {} : {};

  // Extract business_id properly for renewal mode - check multiple possible locations
  const businessId = (isEdit || isRenewal)
    ? baseApplication.business_id ??
      baseApplication.business?.id ??
      baseApplication.business?.business_id ??
      baseApplication.permit_application?.business_id ??
      baseApplication.permit_application?.business?.id ??
      null
    : null;

  // Extract endorsed_by properly - handle both object and ID formats
  const endorsedBy = (isEdit || isRenewal)
    ? baseEndorsement.endorsed_by?.id ??
      baseEndorsement.endorsed_by?.user_id ??
      baseEndorsement.endorsed_by ??
      null
    : null;

  // Extract permit status properly - check multiple possible locations
  const permitStatus = (isEdit || isRenewal)
    ? baseBusinessPermit.status ??
      baseBusinessPermit.permit_status ??
      baseApplication.permit_status ??
      baseApplication.status ??
      "pending"
    : "pending";

  console.log('buildInitialValues - mode:', mode, 'businessId:', businessId, 'permitStatus:', permitStatus, 'baseBusinessPermit:', baseBusinessPermit);

  return {
    application: {
      business_id: businessId,
      application_type: isRenewal
        ? "renewal"
        : isEdit && baseApplication.application_type
        ? baseApplication.application_type
        : "new",
      permit_year: (isEdit || isRenewal) && baseApplication.permit_year
        ? baseApplication.permit_year
        : "",
      remarks: (isEdit || isRenewal) && baseApplication.remarks
        ? baseApplication.remarks
        : "",
    },
    endorsement: {
      endorsed_by: endorsedBy,
      status: (isEdit || isRenewal) && baseEndorsement.status
        ? baseEndorsement.status
        : "pending",
      remarks: (isEdit || isRenewal) && baseEndorsement.remarks
        ? baseEndorsement.remarks
        : "",
    },
    business_permit: {
      issued_date: (isEdit || isRenewal) && baseBusinessPermit.issued_date
        ? baseBusinessPermit.issued_date
        : null,
      status: (isEdit || isRenewal) && baseBusinessPermit.status
        ? baseBusinessPermit.status
        : "pending",
      valid_from: (isEdit || isRenewal) && baseBusinessPermit.valid_from
        ? baseBusinessPermit.valid_from
        : null,
      valid_until: (isEdit || isRenewal) && baseBusinessPermit.valid_until
        ? baseBusinessPermit.valid_until
        : null,
    },
    documents:
      (isEdit || isRenewal) && initialData?.documents
        ? initialData.documents.map((doc) => ({
            requirement_id: doc.requirement_id,
            requirement: doc.requirement,
            file: doc.file,
            verified: doc.verified,
            verified_by: doc.verified_by,
            verified_at: doc.verified_at,
            doc_id: doc.doc_id,
          }))
        : [],
  };
};

/**
 * Runs Yup validation for the current step schema against `values`.
 * Returns a flat errors object keyed by dot-notation paths, e.g.
 * { "application.business_id": "Business is required" }
 */
const validateStep = async (step, values) => {
  const schema = stepSchemas[step];
  if (!schema) return {};
  try {
    await schema.validate(values, { abortEarly: false });
    return {};
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      return err.inner.reduce((acc, e) => {
        if (e.path) acc[e.path] = e.message;
        return acc;
      }, {});
    }
    return {};
  }
};

const deepGet = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const deepSet = (obj, path, value) => {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  keys.forEach((key, idx) => {
    if (idx === keys.length - 1) {
      cur[key] = value;
    } else {
      cur[key] = { ...(cur[key] || {}) };
      cur = cur[key];
    }
  });
  return clone;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const NewBusinessPermitModal = ({
  isOpen,
  onClose,
  mode = "create",
  initialData = null,
  onSubmit,
  renewApplicationId = null,
}) => {
  const tabs = useMemo(
    () => [
      { id: "information", label: "Information", icon: InformationCircleIcon },
      { id: "documents", label: "Documents", icon: DocumentIcon },
      { id: "endorsement", label: "Endorsement", icon: HandThumbUpIcon },
      { id: "validity", label: "Validity", icon: CheckCircleIcon },
    ],
    []
  );

  const [values, setValues] = useState(() => buildInitialValues(mode, initialData));
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFilesByRequirementId, setUploadedFilesByRequirementId] = useState({});
  const [endorsementFile, setEndorsementFile] = useState(null);

  const activeTab = tabs[currentStep]?.id ?? "information";
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tabs.length - 1;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setValues(buildInitialValues(mode, initialData));
      setTouched({});
      setErrors({});
      setCurrentStep(0);
      setUploadedFilesByRequirementId({});
      setEndorsementFile(null);
      
      // Populate existing documents for edit mode and renewal mode
      if ((mode === "edit" || mode === "renewal") && initialData?.documents) {
        const filesByRequirementId = {};
        initialData.documents.forEach((doc) => {
          if (doc.requirement_id && doc.file) {
            filesByRequirementId[doc.requirement_id] = [{
              type: "existing",
              id: doc.doc_id,
              name: doc.file.file_name,
              size: doc.file.size_mb * 1024 * 1024, // Convert MB to bytes
              url: doc.file.url,
              file: null,
              remove: 0,
              _deleted: false,
            }];
          }
        });
        setUploadedFilesByRequirementId(filesByRequirementId);
      }
    }
  }, [isOpen, mode, initialData]);

  // ── API ────────────────────────────────────────────────────────────────────
  const { data: requirementsResponse, isLoading: isRequirementsLoading } = useFetch(
    ["businessPermitDocumentRequirements"],
    async (apiClient) => apiClient.businessPermits.getDocumentRequirements(),
    { enabled: isOpen }
  );
  const documentRequirements = requirementsResponse?.data ?? [];

  const { data: businessesResponse, isLoading: isBusinessesLoading } = useFetch(
    ["businesses"],
    async (apiClient) => apiClient.businessPermits.getBusinesses(),
    { enabled: isOpen }
  );
  const businesses = businessesResponse?.data ?? [];

  const permitMutation = useSubmission(
    [
      mode === "edit"
        ? "updatePermit"
        : mode === "renewal"
        ? "renewPermit"
        : "createPermit",
    ],
    async (apiClient, args) => {
      if (mode === "edit" && initialData?.id) {
        return apiClient.businessPermits.updatePermit(initialData.id, args);
      }

      if (mode === "renewal") {
        const id =
          renewApplicationId ??
          initialData?.application_id ??
          initialData?.permit_application_id ??
          initialData?.id;

        if (!id) {
          throw new Error("Missing permit application id for renewal");
        }

        return apiClient.businessPermits.renew(id, args);
      }

      return apiClient.businessPermits.createPermit(args);
    },
    {
      onSuccess: (result) => {
        if (mode === "edit") {
          showSuccess("Business permit application updated successfully");
        } else if (mode === "renewal") {
          showSuccess("Business permit renewed successfully");
        } else {
          showSuccess("Business permit application created successfully");
        }
        onSubmit?.(result?.data ?? result);
        onClose?.();
      },
      onError: (error) => {
        console.error('Permit submission error:', error);
        
        // Extract error message from response
        const errorMessage = error?.response?.data?.message || 
                           error?.data?.message || 
                           error?.message || 
                           'Failed to submit business permit application. Please try again.';
        
        showError(errorMessage);
      },
    }
  );

  // ── Field helpers ──────────────────────────────────────────────────────────
  const setFieldValue = useCallback((path, value) => {
    setValues((prev) => deepSet(prev, path, value));
    // Clear the error for this field as the user edits it
    setErrors((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }, []);

  const setFieldTouched = useCallback((path) => {
    setTouched((prev) => deepSet(prev, path, true));
  }, []);

  /**
   * Returns the raw error string only when the field has been touched.
   * `errors` is a flat object keyed by dot-notation paths.
   */
  const fieldError = useCallback(
    (path) => {
      const isTouched = deepGet(touched, path);
      const err = errors[path];
      return isTouched && err ? err : null;
    },
    [touched, errors]
  );

  // Wraps the error string into the { message } shape FormInput / FormSelect expect
  const fieldErrorObj = useCallback(
    (path) => {
      const msg = fieldError(path);
      return msg ? { message: msg } : null;
    },
    [fieldError]
  );

  // ── Step helpers ───────────────────────────────────────────────────────────
  const getStepFieldPaths = (step) => {
    switch (step) {
      case 0:
        return [
          "application.business_id",
          "application.application_type",
          "application.permit_year",
          "application.remarks",
        ];
      case 1:
        return ["documents"];
      case 2:
        return [
          "endorsement.endorsed_by",
          "endorsement.status",
          "endorsement.remarks",
        ];
      case 3:
        return [
          "business_permit.issued_date",
          "business_permit.status",
          "business_permit.valid_from",
          "business_permit.valid_until",
        ];
      default:
        return [];
    }
  };

  /** Mark all fields of the current step as touched so errors become visible. */
  const touchStepFields = useCallback((step) => {
    const paths = getStepFieldPaths(step);
    setTouched((prev) => {
      let next = { ...prev };
      paths.forEach((p) => { next = deepSet(next, p, true); });
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Validate the current step. Updates the `errors` state and touches all
   * step fields so messages appear. Returns true if valid.
   */
  const validateCurrentStep = useCallback(
    async (step = currentStep) => {
      touchStepFields(step);
      const stepErrors = await validateStep(step, values);
      // Merge new step errors over existing ones (keep errors from other steps)
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return Object.keys(stepErrors).length === 0;
    },
    [currentStep, values, touchStepFields]
  );

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goToStep = (idx) =>
    setCurrentStep(Math.max(0, Math.min(idx, tabs.length - 1)));

  const handleBack = () => { if (!isFirstStep) goToStep(currentStep - 1); };

  const handleNext = async () => {
    const isValid = await validateCurrentStep(currentStep);
    if (!isValid) return;

    if (!isLastStep) {
      goToStep(currentStep + 1);
      return;
    }

    await handleSubmit(values);
  };

  /** Allow clicking tabs directly but only after validating the current step. */
  const handleTabClick = async (idx) => {
    // Allow navigating backwards freely
    if (idx < currentStep) {
      goToStep(idx);
      return;
    }
    // Validate every step up to the target before jumping forward
    for (let s = currentStep; s < idx; s++) {
      const isValid = await validateCurrentStep(s);
      if (!isValid) return;
    }
    goToStep(idx);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (vals) => {
    const formData = new FormData();

    formData.append("application[business_id]", vals.application.business_id);
    formData.append("application[application_type]", vals.application.application_type);
    formData.append("application[permit_year]", vals.application.permit_year);
    formData.append("application[remarks]", vals.application.remarks);

    if (vals.endorsement.endorsed_by) {
      formData.append("endorsement[endorsed_by]", vals.endorsement.endorsed_by);
    }
    formData.append("endorsement[status]", vals.endorsement.status);
    formData.append("endorsement[remarks]", vals.endorsement.remarks);

    if (vals.business_permit.issued_date) {
      formData.append("business_permit[issued_date]", vals.business_permit.issued_date);
    }
    formData.append("business_permit[status]", vals.business_permit.status);
    if (vals.business_permit.valid_from) {
      formData.append("business_permit[valid_from]", vals.business_permit.valid_from);
    }
    if (vals.business_permit.valid_until) {
      formData.append("business_permit[valid_until]", vals.business_permit.valid_until);
    }

    vals.documents.forEach((doc, idx) => {
      if (doc.file && doc.file instanceof File) {
        formData.append(`documents[${idx}][requirement_id]`, doc.requirement_id);
        formData.append(`documents[${idx}][file]`, doc.file);
      }
    });

    await permitMutation.mutateAsync(formData);
  };

  // ── File handlers ──────────────────────────────────────────────────────────
  const handleRequirementFilesChange = useCallback((requirementId, files) => {
    setUploadedFilesByRequirementId((prev) => {
      const next = { ...prev, [requirementId]: files };
      const documents = Object.entries(next)
        .filter(([, fs]) => fs.length > 0 && fs[0].file)
        .map(([reqId, fs]) => ({ requirement_id: parseInt(reqId), file: fs[0].file }));
      setValues((v) => ({ ...v, documents }));
      // Clear documents error when files are added
      setErrors((e) => { const n = { ...e }; delete n.documents; return n; });
      return next;
    });
  }, []);

  const handleEndorsementFileChange = useCallback(
    (file) => {
      setEndorsementFile(file);
      setFieldValue("endorsement.endorsed_by", file);
    },
    [setFieldValue]
  );

  // ── Tab renderers ──────────────────────────────────────────────────────────
  const renderInformationTab = () => (
    <div className="grid grid-cols-2 gap-4 gap-y-6">
      {/* Application Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Application Type <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-6">
          {["new", "renewal", "closure"]
            .filter((type) => mode !== "renewal" || type === "renewal")
            .map((type) => (
            <label key={type} className="flex items-center">
              <input
                type="radio"
                name="application.application_type"
                value={type}
                checked={values.application.application_type === type}
                onChange={() => {
                  setFieldValue("application.application_type", type);
                  setFieldTouched("application.application_type");
                }}
                onBlur={() => setFieldTouched("application.application_type")}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm font-medium text-gray-700 capitalize">
                {type}
              </span>
            </label>
          ))}
        </div>
        {fieldError("application.application_type") && (
          <p className="text-red-500 text-sm mt-1">
            {fieldError("application.application_type")}
          </p>
        )}
      </div>

      {/* Permit Year */}
      <FormInput
        label="Permit Year"
        type="text"
        placeholder="Enter permit year"
        value={values.application.permit_year || ""}
        onChange={(e) =>
          setFieldValue(
            "application.permit_year",
            e.target.value ? parseInt(e.target.value, 10) : ""
          )
        }
        onBlur={() => setFieldTouched("application.permit_year")}
        error={fieldErrorObj("application.permit_year")}
        required
      />

      {/* Business Name */}
      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Name <span className="text-red-500">*</span>
        </label>
        <select
          className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 ${
            fieldError("application.business_id")
              ? "border-red-400 focus:border-red-500"
              : "border-gray-200 hover:border-gray-300"
          }`}
          value={values.application.business_id ?? ""}
          onChange={(e) => {
            setFieldValue(
              "application.business_id",
              e.target.value ? parseInt(e.target.value, 10) : null
            );
            setFieldTouched("application.business_id");
          }}
          onBlur={() => setFieldTouched("application.business_id")}
        >
          <option value="">
            {isBusinessesLoading ? "Loading businesses..." : "Select registered business"}
          </option>
          {businesses.map((b) => (
            <option key={b.value ?? b.id} value={b.value ?? b.id}>
              {b.label ?? b.name}
            </option>
          ))}
        </select>
        {fieldError("application.business_id") && (
          <p className="text-red-500 text-xs mt-1">
            {fieldError("application.business_id")}
          </p>
        )}
      </div>

      {/* Remarks */}
      <div className="col-span-2">
        <FormInput
          label="Remarks"
          type="textarea"
          placeholder="Enter additional remarks"
          value={values.application.remarks}
          onChange={(e) => setFieldValue("application.remarks", e.target.value)}
          onBlur={() => setFieldTouched("application.remarks")}
          error={fieldErrorObj("application.remarks")}
        />
      </div>
    </div>
  );

  const renderDocumentsTab = () => (
    <div className="space-y-6">
      {isRequirementsLoading && (
        <p className="text-sm text-gray-500">Loading requirements...</p>
      )}
      {!isRequirementsLoading && documentRequirements.length === 0 && (
        <p className="text-sm text-gray-500">No requirements found.</p>
      )}
      {fieldError("documents") && (
        <p className="text-red-500 text-sm">{fieldError("documents")}</p>
      )}
      {!isRequirementsLoading &&
        documentRequirements.map((req) => (
          <FileUploader
            key={req.id}
            label={req.name}
            inputId={`permit-requirement-${req.id}`}
            multiple={false}
            mode={mode}
            initialFiles={uploadedFilesByRequirementId[req.id] ?? []}
            onFilesChange={(files) => handleRequirementFilesChange(req.id, files)}
            size={true}
          />
        ))}
    </div>
  );

  const renderEndorsementTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        {/* Endorsing Officer */}
        <FormSelect
          label="Endorsing Officer"
          placeholder="Select endorsing officer"
          options={[
            { value: 1, label: "Admin User" },
            { value: 2, label: "Barangay Captain" },
            { value: 3, label: "Secretary" },
          ]}
          value={values.endorsement.endorsed_by ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue("endorsement.endorsed_by", value ? parseInt(value) : null);
            setFieldTouched("endorsement.endorsed_by");
          }}
          onBlur={() => setFieldTouched("endorsement.endorsed_by")}
          error={fieldErrorObj("endorsement.endorsed_by")}
        />

        {/* Endorsement Status */}
        <FormSelect
          label="Endorsement Status"
          placeholder="Select status"
          options={[
            { value: "pending", label: "Pending" },
            { value: "returned", label: "Returned" },
            { value: "endorsed", label: "Endorsed" },
          ]}
          value={values.endorsement.status}
          onChange={(e) => {
            setFieldValue("endorsement.status", e.target.value);
            setFieldTouched("endorsement.status");
          }}
          onBlur={() => setFieldTouched("endorsement.status")}
          error={fieldErrorObj("endorsement.status")}
          required
        />
      </div>

      {/* Endorsement Remarks */}
      <FormInput
        label="Endorsement Remarks"
        type="textarea"
        placeholder="Enter additional remarks here"
        value={values.endorsement.remarks}
        onChange={(e) => setFieldValue("endorsement.remarks", e.target.value)}
        onBlur={() => setFieldTouched("endorsement.remarks")}
        error={fieldErrorObj("endorsement.remarks")}
      />
    </div>
  );

  const renderValidityTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <FormInput
          label="Issued Date"
          type="date"
          value={values.business_permit.issued_date || ""}
          onChange={(e) =>
            setFieldValue("business_permit.issued_date", e.target.value)
          }
          onBlur={() => setFieldTouched("business_permit.issued_date")}
          error={fieldErrorObj("business_permit.issued_date")}
        />

        <FormSelect
          label="Permit Status"
          placeholder="Select status"
          options={[
            { value: "pending", label: "Pending" },
            { value: "active", label: "Active" },
            { value: "expired", label: "Expired" },
            { value: "renewed", label: "Renewed" },
            { value: "revoked", label: "Revoked" },
          ]}
          value={values.business_permit.status}
          onChange={(e) => {
            setFieldValue("business_permit.status", e.target.value);
            setFieldTouched("business_permit.status");
          }}
          onBlur={() => setFieldTouched("business_permit.status")}
          error={fieldErrorObj("business_permit.status")}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormInput
          label="Valid From"
          type="date"
          value={values.business_permit.valid_from || ""}
          onChange={(e) =>
            setFieldValue("business_permit.valid_from", e.target.value)
          }
          onBlur={() => setFieldTouched("business_permit.valid_from")}
          error={fieldErrorObj("business_permit.valid_from")}
        />

        <FormInput
          label="Valid Until"
          type="date"
          value={values.business_permit.valid_until || ""}
          onChange={(e) =>
            setFieldValue("business_permit.valid_until", e.target.value)
          }
          onBlur={() => setFieldTouched("business_permit.valid_until")}
          error={fieldErrorObj("business_permit.valid_until")}
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "information": return renderInformationTab();
      case "documents":   return renderDocumentsTab();
      case "endorsement": return renderEndorsementTab();
      case "validity":    return renderValidityTab();
      default:            return renderInformationTab();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${
        mode === "edit" ? "Edit" : mode === "renewal" ? "Renew" : "New"
      } Business Permit`}
      size="4xl"
    >
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab, idx) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(idx)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">{renderTabContent()}</div>

      {/* Footer */}
      <div className="flex justify-end gap-3 mt-6">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={isFirstStep || permitMutation.isLoading}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={permitMutation.isLoading}
        >
          {isLastStep
            ? mode === "edit"
              ? "Update"
              : mode === "renewal"
              ? "Renew"
              : "Submit"
            : "Next"}
        </Button>
      </div>
    </Modal>
  );
};

export default NewBusinessPermitModal;